import {
  Brush as BrushIcon,
  EditOff as EditOffIcon,
  Redo as RedoIcon,
  Undo as UndoIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Grid, IconButton, Paper, Slider, Stack, TextareaAutosize, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Circle, Image as KonvaImage, Layer, Line, Stage } from 'react-konva';
import HttpServices from 'src/services/httpService';
import { postsModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useResellerLimitDialog } from 'src/components/reseller-limit-context/resellerLimitContext';

const CANVAS_WIDTH = 900;
const CANVAS_HEIGHT = window.innerHeight * 0.6;

const AIImageEditor = ({ mediaUrl, handleEditedImage }) => {
  const { showToast } = useToaster();
  const theme = useTheme();
  const { showResellerLimitDialog } = useResellerLimitDialog();
  const { t: translate } = useTranslation();

  const [imageUrl, setImageUrl] = useState(mediaUrl);
  const [image, setImage] = useState(null);
  const [previousImageUrl, setPreviousImageUrl] = useState(mediaUrl);
  const [grayscaleImage, setGrayscaleImage] = useState(null);
  const [lines, setLines] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(20);
  const [scaleInfo, setScaleInfo] = useState({ scale: 1, offsetX: 0, offsetY: 0 });
  const [prompt, setPrompt] = useState('');
  const [eraserMode, setEraserMode] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isImageEditingOn, setImageEditingOn] = useState(false);
  const [imageHistory, setImageHistory] = useState([mediaUrl]);

  const stageRef = useRef();

  useEffect(() => {
    const img = new window.Image();
    img.crossOrigin = 'Anonymous';
    img.src = imageUrl;
    img.onload = () => {
      setImage(img);

      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = img.width;
      tempCanvas.height = img.height;
      const ctx = tempCanvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, img.width, img.height);
      ctx.putImageData(imageData, 0, 0);

      const grayImg = new window.Image();
      grayImg.src = tempCanvas.toDataURL();
      grayImg.onload = () => setGrayscaleImage(grayImg);

      const scale = Math.min(CANVAS_WIDTH / img.width, CANVAS_HEIGHT / img.height);
      const offsetX = (CANVAS_WIDTH - img.width * scale) / 2;
      const offsetY = (CANVAS_HEIGHT - img.height * scale) / 2;
      setScaleInfo({ scale, offsetX, offsetY });
    };
  }, [imageUrl]);

  const setToInitialStage = (url) => {
    if (url === imageUrl) {
      return;
    }
    setImage(null);
    setGrayscaleImage(null);
    setLines([]);
    setRedoStack([]);
    setIsDrawing(false);
    setBrushSize(20);
    setScaleInfo({ scale: 1, offsetX: 0, offsetY: 0 });
    setPrompt('');
    setEraserMode(false);
    setMousePos({ x: 0, y: 0 });
    setImageUrl(url);
  };

  const getScaledPoint = (point) => {
    const { scale, offsetX, offsetY } = scaleInfo;
    return {
      x: (point.x - offsetX) / scale,
      y: (point.y - offsetY) / scale,
    };
  };

  const isInsideImage = (point) => {
    return point.x >= 0 && point.y >= 0 && point.x <= image?.width && point.y <= image?.height;
  };

  const handleMouseDown = () => {
    setIsDrawing(true);
    setLines((prev) => [
      ...prev,
      {
        tool: eraserMode ? 'eraser' : 'brush',
        points: [],
        size: brushSize,
      },
    ]);
    setRedoStack([]);
  };

  const handleMouseMove = (e) => {
    const stage = stageRef.current;
    const pointer = stage.getPointerPosition();
    if (pointer) setMousePos(pointer);

    if (!isDrawing) return;
    const scaled = getScaledPoint(pointer);

    if (!isInsideImage(scaled)) return;

    setLines((prevLines) => {
      const newLines = [...prevLines];
      newLines[newLines.length - 1].points.push(scaled);
      return newLines;
    });
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  const handleUndo = () => {
    if (lines.length === 0) return;
    const newLines = [...lines];
    const popped = newLines.pop();
    setLines(newLines);
    setRedoStack([...redoStack, popped]);
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const popped = redoStack.pop();
    setLines([...lines, popped]);
    setRedoStack([...redoStack]);
  };

  const dataURLToFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const handleExport = () => {
    if (!image) return;
    const width = image.width;
    const height = image.height;
    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = width;
    offscreenCanvas.height = height;
    const ctx = offscreenCanvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);

    lines.forEach((line) => {
      if (line.points.length < 2) return;
      ctx.beginPath();
      ctx.strokeStyle = line.tool === 'eraser' ? '#fff' : 'black';
      const normalizedBrushSize = line.size / scaleInfo.scale;
      ctx.lineWidth = normalizedBrushSize;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.moveTo(line.points[0].x, line.points[0].y);
      for (let i = 1; i < line.points.length; i++) {
        ctx.lineTo(line.points[i].x, line.points[i].y);
      }
      ctx.stroke();
    });

    setImageEditingOn(true);
    const uri = offscreenCanvas.toDataURL('image/png');
    const file = dataURLToFile(uri, 'ideogram-mask.png');

    let uploadFilesProps = {
      files: [file],
      setFileUploadprogress: () => {},
      successCallback,
      failureCallback,
    };
    CommonHelper.UploadMediaFilesToServer({ ...uploadFilesProps });
  };

  const successCallback = (data, message) => {
    EditImage(data[0]?.media_url);
  };

  const EditImage = (maskImageUrl) => {
    let editImageProps = {
      prompt: prompt,
      uploaded_image_url: imageUrl,
      mask_image_url: maskImageUrl,
    };
    let postProps = {
      url: postsModuleURL.aiEditImage,
      body: editImageProps,
      successCallback: editImageSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const editImageSuccessCallback = (data, message) => {
    let iHistory = [...imageHistory];
    iHistory.push(data?.media_data[0]?.media_url);
    setImageEditingOn(false);
    setPreviousImageUrl(imageUrl);
    setImageHistory([...iHistory]);
    setToInitialStage(data?.media_data[0]?.media_url);
  };

  const failureCallback = (message) => {
    setImageEditingOn(false);
    if ( message && !showResellerLimitDialog(message)) {
      showToast(message, 'e');
    }
  };

  const clipFunc = useCallback(
    (ctx) => {
      lines
        .filter((line) => line.tool !== 'eraser')
        .forEach((line) => {
          ctx.beginPath();
          line.points.forEach((point, i) => {
            const x = point.x * scaleInfo.scale + scaleInfo.offsetX;
            const y = point.y * scaleInfo.scale + scaleInfo.offsetY;
            if (i === 0) {
              ctx.moveTo(x, y);
            } else {
              ctx.lineTo(x, y);
            }
          });
          ctx.closePath();
          ctx.clip();
        });
    },
    [lines, scaleInfo]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.ctrlKey && e.key === 'z') {
        handleUndo();
      } else if (e.ctrlKey && e.key === 'y') {
        handleRedo();
      }
    },
    [handleUndo, handleRedo]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={2}>
          <Paper>
            <Stack spacing={2} mt={2} alignItems="center">
              <Typography variant="caption">{translate('common.brush_size', 'Brush Size')}</Typography>
              <Slider
                min={5}
                max={100}
                value={brushSize}
                onChange={(e, val) => setBrushSize(val)}
                orientation="vertical"
                sx={{ height: 100 }}
              />
              <IconButton onClick={() => setEraserMode(false)} color={!eraserMode ? 'primary' : 'default'}>
                <BrushIcon />
              </IconButton>
              <Typography variant="caption">{translate('common.brush', 'Brush')}</Typography>
              <IconButton onClick={() => setEraserMode(true)} color={eraserMode ? 'primary' : 'default'}>
                <EditOffIcon />
              </IconButton>
              <Typography variant="caption">{translate('common.eraser', 'Eraser')}</Typography>

              <IconButton onClick={handleUndo}>
                <UndoIcon />
              </IconButton>
              <Typography variant="caption">{translate('common.undo', 'Undo')}</Typography>
              <IconButton onClick={handleRedo}>
                <RedoIcon />
              </IconButton>
              <Typography variant="caption">{translate('common.redo', 'Redo')}</Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Box
            sx={{
              border: '2px solid #ccc',
              borderRadius: '10px',
              p: 1,
              backgroundColor: '#f9f9f9',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Stage
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              onMouseDown={handleMouseDown}
              onMousemove={handleMouseMove}
              onMouseup={handleMouseUp}
              ref={stageRef}
              style={{
                background: '#fff',
                borderRadius: '8px',
                maxWidth: '100%',
                maxHeight: '100%',
              }}
            >
              <Layer>
                {image && (
                  <KonvaImage
                    image={image}
                    x={scaleInfo.offsetX}
                    y={scaleInfo.offsetY}
                    width={image.width * scaleInfo.scale}
                    height={image.height * scaleInfo.scale}
                  />
                )}
              </Layer>

              <Layer>
                {grayscaleImage && (
                  <KonvaImage
                    image={grayscaleImage}
                    x={scaleInfo.offsetX}
                    y={scaleInfo.offsetY}
                    width={grayscaleImage.width * scaleInfo.scale}
                    height={grayscaleImage.height * scaleInfo.scale}
                    clipFunc={clipFunc}
                  />
                )}
              </Layer>

              <Layer>
                {lines.map((line, i) => (
                  <Line
                    key={i}
                    points={line.points.flatMap((p) => [
                      p.x * scaleInfo.scale + scaleInfo.offsetX,
                      p.y * scaleInfo.scale + scaleInfo.offsetY,
                    ])}
                    stroke={line.tool === 'eraser' ? '#fff' : 'black'}
                    strokeWidth={line.size}
                    tension={0.5}
                    lineCap="round"
                    globalCompositeOperation={line.tool === 'eraser' ? 'destination-out' : 'source-over'}
                  />
                ))}
              </Layer>

              <Layer>
                <Circle
                  x={mousePos.x}
                  y={mousePos.y}
                  radius={brushSize / 2}
                  stroke="blue"
                  strokeWidth={2}
                  dash={[6, 4]}
                  opacity={0.5}
                />
              </Layer>
            </Stage>
          </Box>
        </Grid>

        <Grid item xs={12} md={2}>
          <Paper>
            <Stack spacing={2} mt={2} alignItems="center">
              <Box
                sx={{
                  maxHeight: 550,
                  overflowY: 'auto',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                {imageHistory?.length > 0 &&
                  imageHistory.map((image, index) => (
                    <Box
                      key={index}
                      onClick={() => {
                        setToInitialStage(image);
                      }}
                      sx={{
                        border:
                          imageUrl === image ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                        borderRadius: 1,
                        cursor: 'pointer',
                        p: 0.5,
                      }}
                    >
                      <img
                        src={image}
                        style={{ objectFit: 'cover' }}
                        width={100}
                        height={100}
                        alt={`history-${index}`}
                      />
                    </Box>
                  ))}
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={2}
        style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}
      >
        <div style={{ position: 'relative', width: '50%' }}>
          <TextareaAutosize
            minRows={2}
            maxRows={4}
            placeholder={translate('common.enter_prompt_for_edit_image', 'Enter prompt for edit image')}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              paddingBottom: '48px',
              borderRadius: '8px',
              border: '1px solid #ccc',
              backgroundColor: '#f9f9f9',
              fontSize: '16px',
              resize: 'none',
              fontFamily: 'Public sans, sans-serif',
              outline: 'none',
              transition: 'box-shadow 0.3s ease',
              boxShadow: prompt?.trim() ? `0 0 8px 2px ${theme.palette.primary.main}` : 'none',
            }}
          />
          <LoadingButton
            variant="outlined"
            loading={isImageEditingOn}
            startIcon={<SendIcon />}
            onClick={handleExport}
            style={{
              position: 'absolute',
              bottom: '12px',
              right: '10px',
              textTransform: 'none',
              padding: '4px 12px',
              fontSize: '14px',
              minWidth: '50px',
              zIndex: 2,
            }}
          >
            Edit Image
          </LoadingButton>
        </div>
      </Stack>
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={2}
        style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}
      >
        <LoadingButton
          variant="contained"
          style={{
            whiteSpace: 'nowrap',
            textTransform: 'none',
          }}
          onClick={() => handleEditedImage(mediaUrl, imageUrl)}
        >
          Save Edited File
        </LoadingButton>
      </Stack>
    </Box>
  );
};

export default AIImageEditor;
